import { useAppDispatch } from "./reduxHook";
import { usePatchCaptureMutation } from "../actions/capture";
import { catalogApi } from "../actions/catalogInQuery";
import { upsertCatalogItems } from "../reducers/catalogItemsSlice";
import { CatalogItem, listCatalogRequest } from "../types";
import { usePatchDetectionMutation } from "../actions/detection";

interface UseBookmarkToggleProps {
  catalogItem: CatalogItem;
  queryParams?: listCatalogRequest;
}

export const useBookmarkToggle = ({
  catalogItem,
  queryParams,
}: UseBookmarkToggleProps) => {
  const dispatch = useAppDispatch();
  const [patchDetection, { isLoading: isPatchingDetection }] =
    usePatchDetectionMutation();
  const [patchCapture, { isLoading: isPatchingCapture }] =
    usePatchCaptureMutation();

  const isBookmarking = isPatchingDetection || isPatchingCapture;

  const handleToggleBookmark = async () => {
    const { source, is_bookmarked, id } = catalogItem;
    const newBookmarkState = !is_bookmarked;

    let patchResult: any;

    // 1. Optimistic Update for listCatalog
    if (queryParams) {
      patchResult = dispatch(
        catalogApi.util.updateQueryData("listCatalog", queryParams, (draft) => {
          const item = draft.items.find((i) => {
            return i.id === id;
          });
          if (item) {
            item.is_bookmarked = newBookmarkState;
          }
        }),
      );
    }

    try {
      if (source === "detection") {
        await patchDetection({
          id,
          data: { is_bookmarked: newBookmarkState },
        }).unwrap();
      } else if (source === "capture") {
        await patchCapture({
          id,
          data: { is_bookmarked: newBookmarkState },
        }).unwrap();
      }

      // 2. Update individual items slice for catalog download
      dispatch(
        upsertCatalogItems([
          {
            ...catalogItem,
            is_bookmarked: newBookmarkState,
          } as any,
        ]),
      );
    } catch (error) {
      // Rollback optimistic update
      if (patchResult) {
        patchResult.undo();
      }
    }
  };

  return {
    isBookmarking,
    handleToggleBookmark,
  };
};
